import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  RichTextEditorComponent,
  Inject,
  Toolbar,
  HtmlEditor,
  Image,
  Link,
  Table,
  QuickToolbar,
} from "@syncfusion/ej2-react-richtexteditor";
import "./TogglableContentInput.css";

const TogglableContentInput = forwardRef(({ value, onChange }, ref) => {
  const [content, setContent] = useState(value || "");
  const editorRef = useRef(null);

  useEffect(() => {
    setContent(value || "");
  }, [value]);

  /** 🧹 Clean up pasted or edited HTML */
  const cleanHtml = (html) => {
    if (!html) return "";

    // Remove Word conditional comments that wrap bullets - keep inner content (e.g. "• ")
    html = html.replace(
      /<!--\[if\s+!supportLists\]-->([\s\S]*?)<!--\[endif\]-->/gi,
      "$1"
    );

    // Remove Word o:p tags (with any content including &nbsp;)
    html = html.replace(/<o:p[^>]*>[\s\S]*?<\/o:p>/gi, "");

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    if (!doc.body) return "";

    // Remove all attributes except data-number and content-bullet-list class
    doc.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        const keep =
          attr.name === "data-number" ||
          (attr.name === "class" &&
            el.tagName === "UL" &&
            attr.value === "content-bullet-list");
        if (!keep) el.removeAttribute(attr.name);
      });
    });

    // Replace bullet spans (Word uses Symbol font) with standard • before unwrapSpans strips the font
    doc.querySelectorAll("span").forEach((span) => {
      const text = span.textContent.trim();
      if (/^[\u00B7\u2022\u2023\u2219\u2024\u2043\u02D9]$/.test(text)) {
        span.replaceWith(doc.createTextNode("•"));
      }
    });

    // Format lists
    doc.querySelectorAll("li > p").forEach((p) => {
      const li = p.parentNode;
      while (p.firstChild) li.insertBefore(p.firstChild, p);
      p.remove();
    });

    // Unwrap spans in block elements
    const unwrapSpans = (element) => {
      element.querySelectorAll("span").forEach((span) => {
        const parentTag = span.parentNode?.tagName?.toLowerCase();
        if (
          ["p", "h1", "h2", "h3", "h4", "h5", "h6", "li"].includes(parentTag)
        ) {
          span.replaceWith(...span.childNodes);
        }
      });
    };
    unwrapSpans(doc.body);

    // Remove outer span wrapping everything
    if (
      doc.body.childNodes.length === 1 &&
      doc.body.firstChild.tagName === "SPAN"
    ) {
      const span = doc.body.firstChild;
      while (span.firstChild) {
        doc.body.insertBefore(span.firstChild, span);
      }
      span.remove();
    }

    // Wrap plain text with <p> tags
    doc.body.childNodes.forEach((node) => {
      if (
        node.nodeType === Node.TEXT_NODE &&
        node.textContent.trim() !== "" &&
        node.parentNode === doc.body
      ) {
        // Do not wrap iframe or script or other block-html around text
        const p = doc.createElement("p");
        p.textContent = node.textContent;
        node.replaceWith(p);
      }
    });

    // Unwrap iframe if inside <p>
    doc.querySelectorAll("p > iframe").forEach((iframe) => {
      iframe.parentNode.replaceWith(iframe);
    });

    let result = doc.body.innerHTML.trim();

    // Normalize bullet-like chars (· ‣ ∙) to standard bullet (•) at start of block content (after > or newline)
    result = result.replace(/([>\n])\s*[\u00B7\u2023\u2219](\s+)/g, "$1•$2");
    result = result.replace(/([>\n])\s*&#183;(\s+)/g, "$1•$2");
    result = result.replace(/([>\n])\s*&middot;(\s+)/gi, "$1•$2");

    // Remove Word's empty Office namespace tags
    result = result.replace(/<o:p[^>]*><\/o:p>/gi, "");

    // Add space after bullet when directly adjacent to text (•4 → • 4)
    result = result.replace(/•([^\s])/g, "• $1");

    // Convert bullet paragraphs to semantic list for indentation and spacing
    result = convertBulletParagraphsToList(result);

    return result;
  };

  /** Convert <p>• text</p> to <ul><li>text</li></ul> for proper display */
  const convertBulletParagraphsToList = (html) => {
    // Match one or more consecutive <p>• ...</p> (works on raw HTML, any structure)
    return html.replace(/(<p>[\s\u00A0]*•[\s\u00A0]*[\s\S]*?<\/p>\s*)+/gi, (match) => {
      const items = [];
      let m;
      const re = /<p>[\s\u00A0]*•[\s\u00A0]*([\s\S]*?)<\/p>/gi;
      while ((m = re.exec(match)) !== null) {
        const content = m[1].trim() || "<br>";
        items.push(`<li>${content}</li>`);
      }
      return items.length ? `<ul class="content-bullet-list">${items.join("")}</ul>` : match;
    });
  };

  useImperativeHandle(ref, () => ({
    /** Get current editor value with cleanup applied - use before Update to avoid timing issues */
    getCurrentValue() {
      const editor = editorRef.current;
      if (!editor) return value || "";
      editor.updateValue?.();
      const raw = editor.value ?? value ?? "";
      return cleanHtml(raw);
    },
  }));

  /** Handle editor changes */
  const handleEditorChange = (args) => {
    let newValue = args.value || "";
    newValue = cleanHtml(newValue);
    setContent(newValue);
    onChange(newValue);

    const restoreSelection = () => {
      try {
        const editor = editorRef.current;
        if (!editor) return;
        editor.contentModule
          .getDocument()
          .defaultView.getSelection()
          .collapseToEnd();
      } catch {}
    };

    restoreSelection();
  };

  /** Force change-like flow: get editor value, clean it, push back. Call after paste. */
  const forceChangeLikeCleanup = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.updateValue?.();
    const raw = editor.value ?? content ?? "";
    if (!raw) return;
    const cleaned = cleanHtml(raw);
    editor.value = cleaned;
    setContent(cleaned);
    onChange(cleaned);
  };

  /** Only sanitize pasted content */
  const handleBeforePasteCleanup = (args) => {
    args.value = cleanHtml(args.value);
  };

  const handlePaste = () => {
    setTimeout(forceChangeLikeCleanup, 50);
  };

  /** Force sync and cleanup after paste - runs when paste is complete */
  const handleAfterPasteCleanup = (args) => {
    args.value = cleanHtml(args.value ?? "");
    setTimeout(forceChangeLikeCleanup, 50);
  };

  /** Flush and cleanup when editor loses focus - ensures content is cleaned before Update/Add */
  const handleBlur = () => {
    const editor = editorRef.current;
    if (!editor) return;
    editor.updateValue?.();
    const raw = editor.value ?? content ?? "";
    if (!raw) return;
    const cleaned = cleanHtml(raw);
    editor.value = cleaned;
    setContent(cleaned);
    onChange(cleaned);
  };

  /** Insert <br> button */
  const insertBreak = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const doc = editor.contentModule.getDocument();
    const selection = doc.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    const br = doc.createElement("br");
    range.insertNode(br);

    const parent = br.parentNode;
    if (parent.tagName === "P" && parent.textContent === "") {
      parent.replaceWith(...parent.childNodes);
    }

    range.setStartAfter(br);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    editor.focusIn();
  };

  return (
    <div className="content-editor">
      <RichTextEditorComponent
        ref={editorRef}
        value={content}
        change={handleEditorChange}
        blur={handleBlur}
        beforePasteCleanup={handleBeforePasteCleanup}
        paste={handlePaste}
        afterPasteCleanup={handleAfterPasteCleanup}
        enterKey="BR"
        formatting={{ defaultTag: null, removeFormats: true }}
        pasteCleanup={{
          keepFormat: false,
          cleanCSS: true,
          cleanElements: true,
          cleanAttrs: [],
          prompt: false,
        }}
        saveInterval={1000}
        toolbarSettings={{
          items: [
            "Bold",
            "Italic",
            "Underline",
            "|",
            "Formats",
            "Alignments",
            "OrderedList",
            "UnorderedList",
            "|",
            "CreateTable",
            "CreateLink",
            "Image",
            "SourceCode",
            "|",
            {
              tooltipText: "Insert Break",
              prefixIcon: "e-icons e-break",
              click: insertBreak,
              type: "Button",
            },
            "Undo",
            "Redo",
          ],
        }}
        format={{
          types: [
            { text: "Paragraph", value: "P" },
            { text: "Heading 1", value: "H1" },
            { text: "Heading 2", value: "H2" },
            { text: "Heading 3", value: "H3" },
            { text: "Heading 4", value: "H4" },
            { text: "Heading 5", value: "H5" },
            { text: "Heading 6", value: "H6" },
          ],
        }}
        height="300px"
      >
        <Inject
          services={[Toolbar, HtmlEditor, Image, Link, Table, QuickToolbar]}
        />
      </RichTextEditorComponent>
    </div>
  );
});

TogglableContentInput.displayName = "TogglableContentInput";

export default TogglableContentInput;

// // Settings for ordered list item formatting

// const handleOrderedListClick = () => {
//   const editor = editorRef.current;
//   if (!editor) return;

//   const doc = editor.contentModule.getDocument();
//   const sel = doc.getSelection();

//   if (!sel.rangeCount) return;
//   const range = sel.getRangeAt(0);
//   const node = range.startContainer;

//   // Find current paragraph or block
//   let block = node.nodeType === 1 ? node : node.parentNode;
//   while (block && !["P", "DIV", "BR"].includes(block.tagName)) {
//     block = block.parentNode;
//   }

//   // If user is on an empty new line, force a NEW <ol><li>
//   if (block && block.textContent.trim() === "") {
//     const ol = doc.createElement("ol");
//     const li = doc.createElement("li");
//     li.innerHTML = "<br>"; // so caret appears correctly
//     ol.appendChild(li);

//     block.replaceWith(ol);

//     // Move caret into <li>
//     const newRange = doc.createRange();
//     newRange.setStart(li, 0);
//     newRange.collapse(true);
//     sel.removeAllRanges();
//     sel.addRange(newRange);

//     return;
//   }

//   // Otherwise, default behavior
//   editor.executeCommand("insertOrderedList");
// };

// toolbarSettings={{
//   items: [
//     "Bold",
//     "Italic",
//     "Underline",
//     "|",
//     "Formats",
//     "Alignments",

//     // Replace this: "OrderedList",
//     {
//       tooltipText: "Ordered List",
//       prefixIcon: "e-icons e-list-ol",
//       click: handleOrderedListClick,
//       type: "Button",
//     },

//     "UnorderedList",
//     "|",
//     "CreateTable",
//     "CreateLink",
//     "Image",
//     "SourceCode",
//     "|",
//     {
//       tooltipText: "Insert Break",
//       prefixIcon: "e-icons e-break",
//       click: insertBreak,
//       type: "Button",
//     },
//     "Undo",
//     "Redo",
//   ],
// }}
